import type { DirectedPath } from "./contracts.js";
import { isDrawableMotionFeature } from "./motion.js";
import { projectMatchOnPath, selectDirectedVariant } from "./track.js";

export type TrainDecision =
  | "rejectedValidation"
  | "rejectedVariant"
  | "rejectedPathLength"
  | "culled"
  | "drawn";
export type TrainDecisionCounts = Record<TrainDecision, number>;
export const emptyTrainDecisionCounts = (): TrainDecisionCounts => ({
  rejectedValidation: 0,
  rejectedVariant: 0,
  rejectedPathLength: 0,
  culled: 0,
  drawn: 0,
});

export interface TrainPipelineFeature {
  id: string;
  path: readonly [number, number][];
  timestamps?: readonly number[];
}

export interface LiveTrainFeature extends TrainPipelineFeature {
  routeId: string;
  destination?: string;
  nextStop?: string;
  nextStopAt?: number;
}

export class TrainFeatureValidationError extends Error {}

const invalidFeature = (message: string): never => {
  throw new TrainFeatureValidationError(message);
};

export function readTrainFeature(value: unknown): LiveTrainFeature {
  if (typeof value !== "object" || value === null) invalidFeature("train feature must be an object");
  const feature = value as Record<string, unknown>;
  const id = feature.id;
  if (typeof id !== "string" || id.length === 0) invalidFeature("train id must be a string");
  if (typeof feature.props !== "object" || feature.props === null) invalidFeature("train props must be an object");
  if (typeof feature.geometry !== "object" || feature.geometry === null) invalidFeature("train geometry must be an object");
  const props = feature.props as Record<string, unknown>;
  const geometry = feature.geometry as Record<string, unknown>;
  const routeId = props.routeId;
  if (typeof routeId !== "string" || routeId.length === 0) invalidFeature("train routeId must be a string");
  if (!Array.isArray(geometry.coordinates)) invalidFeature("train coordinates must be an array");
  if (props.timestamps !== undefined && !Array.isArray(props.timestamps)) invalidFeature("train timestamps must be an array");
  return {
    id: id as string,
    path: geometry.coordinates as [number, number][],
    timestamps: props.timestamps as number[] | undefined,
    routeId: routeId as string,
    destination: typeof props.destination === "string" ? props.destination : undefined,
    nextStop: typeof props.nextStop === "string" ? props.nextStop : undefined,
    nextStopAt: typeof props.nextStopAt === "number" ? props.nextStopAt : undefined,
  };
}

export function advanceProjectedHead(
  decision: { path?: DirectedPath; head?: [number, number] },
  previousDistance?: number,
): number | undefined {
  if (!decision.path || !decision.head) return previousDistance;
  // A culled train has no visible continuity to preserve. Resetting to the
  // current head prevents the normal 200 m hysteresis from freezing it offscreen.
  return projectMatchOnPath(decision.head, decision.path).distance;
}

/** Runs the cheap, ordered gates shared by live rendering and fixture coverage. */
export function decideTrain(
  feature: TrainPipelineFeature,
  candidates: readonly DirectedPath[],
  cachedPath: DirectedPath | undefined,
  consistLength: number,
  motionHead: () => [number, number],
  nearViewport: (head: [number, number]) => boolean,
): { decision: TrainDecision; path?: DirectedPath; head?: [number, number] } {
  if (!feature.timestamps || !isDrawableMotionFeature({
    id: feature.id,
    path: feature.path,
    timestamps: feature.timestamps,
  })) return { decision: "rejectedValidation" };
  const head = motionHead();
  // Variant selection projects the entire live path over every candidate.
  // Reject offscreen trains using their interpolated live head first.
  if (!nearViewport(head)) return { decision: "culled", head, path: cachedPath };
  const path = cachedPath ?? selectDirectedVariant(feature.path, candidates);
  if (!path) return { decision: "rejectedVariant" };
  if ((path.sMeters.at(-1) ?? 0) < consistLength) {
    return { decision: "rejectedPathLength", path };
  }
  return { decision: "drawn", path, head };
}
